import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { templatesApi, documentsApi, bullhornApi } from '../services/api';
import { 
  Send, 
  Search, 
  User,
  FileText,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  ArrowRight,
  ArrowLeft,
  Building2,
  Plus,
  X,
  Clock,
  FileCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

// Bullhorn entity colors (from Bullhorn official brand guidelines)
const ENTITY_COLORS = {
  candidate: { bg: 'bg-[#4CAF50]', text: 'text-white', label: 'Candidate', lightBg: 'bg-[#4CAF50]/10', border: 'border-[#4CAF50]', iconColor: 'text-[#4CAF50]' },  // Green
  contact: { bg: 'bg-[#F9A825]', text: 'text-white', label: 'Contact', lightBg: 'bg-[#F9A825]/10', border: 'border-[#F9A825]', iconColor: 'text-[#F9A825]' },  // Yellow/Orange
  placement: { bg: 'bg-[#1a4a5e]', text: 'text-white', label: 'Placement', lightBg: 'bg-[#1a4a5e]/10', border: 'border-[#1a4a5e]', iconColor: 'text-[#1a4a5e]' },  // Deep Teal
};

// Fallback used when a template has an unrecognised or missing entity_type
const DEFAULT_ENTITY_COLOR = {
  bg: 'bg-slate-500', text: 'text-white', label: 'Other',
  lightBg: 'bg-slate-500/10', border: 'border-slate-300', iconColor: 'text-slate-500',
};
const colorFor = (key) => ENTITY_COLORS[key] || DEFAULT_ENTITY_COLOR;

const STEPS = [
  { id: 'templates', title: 'Select Documents', icon: FileText },
  { id: 'recipient', title: 'Choose Recipient', icon: User },
  { id: 'options', title: 'Options', icon: Clock },
  { id: 'review', title: 'Review & Send', icon: Send },
];

export default function BatchSendPage() {
  const navigate = useNavigate();
  const { getTenantPath } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Selected templates
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  
  // Recipient data
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [recipient, setRecipient] = useState({
    bullhorn_id: '',
    name: '',
    email: '',
    entity_type: 'candidate',
    title: '',
    company_id: '',
    company_name: '',
  });
  
  // Options
  const [expiryHours, setExpiryHours] = useState(72);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await templatesApi.list();
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const toggleTemplate = (template) => {
    setSelectedTemplates(prev => {
      const exists = prev.find(t => t.id === template.id);
      if (exists) {
        return prev.filter(t => t.id !== template.id);
      } else if (prev.length < 10) {
        return [...prev, template];
      } else {
        toast.error('Maximum 10 documents per batch');
        return prev;
      }
    });
  };

  const searchRecipients = async () => {
    if (searchQuery.length < 2) return;
    setSearching(true);
    try {
      // Determine entity type from selected templates (use first template's type)
      const entityType = selectedTemplates[0]?.entity_type || 'candidate';
      
      if (entityType === 'contact') {
        const response = await bullhornApi.searchContacts(searchQuery);
        setSearchResults(response.data.contacts || []);
      } else {
        const response = await bullhornApi.searchCandidates(searchQuery);
        setSearchResults(response.data.candidates || []);
      }
    } catch (error) {
      toast.error('Failed to search recipients');
    } finally {
      setSearching(false);
    }
  };

  const selectRecipient = (r) => {
    const entityType = selectedTemplates[0]?.entity_type || 'candidate';
    setRecipient({
      bullhorn_id: r.id,
      name: r.name || `${r.firstName || ''} ${r.lastName || ''}`.trim(),
      email: r.email || '',
      entity_type: entityType,
      title: r.title || '',
      company_id: r.company?.id || '',
      company_name: r.company?.name || '',
    });
    setSearchResults([]);
    setSearchQuery('');
  };

  const handleSend = async () => {
    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one document');
      return;
    }
    if (!recipient.name || !recipient.email) {
      toast.error('Please enter recipient information');
      return;
    }

    setSending(true);
    try {
      const response = await documentsApi.batchSend({
        template_ids: selectedTemplates.map(t => t.id),
        recipient: {
          bullhorn_id: recipient.bullhorn_id || null,
          name: recipient.name,
          email: recipient.email,
          entity_type: recipient.entity_type,
          title: recipient.title || null,
          company_id: recipient.company_id || null,
          company_name: recipient.company_name || null,
        },
        expiry_hours: expiryHours,
        message: message || null,
        requires_approval: requiresApproval,
      });
      
      toast.success(`Successfully sent ${selectedTemplates.length} documents!`);
      navigate(getTenantPath('/documents?highlight=latest'));
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to send documents';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const entityType = selectedTemplates[0]?.entity_type || 'candidate';
  const entityColor = colorFor(entityType);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="batch-send-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-bold mb-2">Send Multiple Documents</h1>
          <p className="text-muted-foreground">Send multiple documents to the same recipient at once</p>
        </div>

        {/* Steps Progress */}
        <div className="flex items-center justify-between mb-8 px-4">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div 
                className={`flex items-center gap-2 ${
                  index <= currentStep ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  index < currentStep 
                    ? 'bg-primary text-white' 
                    : index === currentStep 
                      ? 'bg-primary/10 text-primary border-2 border-primary' 
                      : 'bg-muted text-muted-foreground'
                }`}>
                  {index < currentStep ? <CheckCircle2 className="w-4 h-4" /> : index + 1}
                </div>
                <span className="text-sm font-medium hidden md:block">{step.title}</span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`w-12 lg:w-24 h-0.5 mx-2 ${
                  index < currentStep ? 'bg-primary' : 'bg-border'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card className="neumorphic">
          <CardContent className="p-6">
            {/* Step 1: Select Documents */}
            {currentStep === 0 && (
              <div className="animate-fade-in" data-testid="step-templates">
                <h2 className="font-heading text-xl font-semibold mb-4">Select documents to send</h2>
                <p className="text-muted-foreground mb-6">Choose up to 10 documents to send to the same recipient</p>
                
                {selectedTemplates.length > 0 && (
                  <div className="mb-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">Selected ({selectedTemplates.length}/10)</span>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedTemplates([])}>
                        Clear all
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplates.map((t) => (
                        <div 
                          key={t.id} 
                          className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border border-primary/30 text-sm"
                        >
                          <span className="truncate max-w-[150px]">{t.name}</span>
                          <button onClick={() => toggleTemplate(t)} className="text-muted-foreground hover:text-foreground">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(() => {
                  // Same 3-column entity-grouped + alphabetical layout
                  // as the single-send template picker, with Checkboxes
                  // instead of radio-style selection so users can pick
                  // multiple docs across columns.
                  const groups = { candidate: [], placement: [], contact: [] };
                  for (const t of templates) {
                    const k = (t.entity_type || 'candidate');
                    (groups[k] || groups.candidate).push(t);
                  }
                  for (const k of Object.keys(groups)) {
                    groups[k].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                  }
                  const cols = [
                    { key: 'candidate', label: 'Candidate', items: groups.candidate },
                    { key: 'placement', label: 'Placement', items: groups.placement },
                    { key: 'contact', label: 'Contact', items: groups.contact },
                  ];
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {cols.map((col) => {
                        const ec = ENTITY_COLORS[col.key] || DEFAULT_ENTITY_COLOR;
                        return (
                          <div key={col.key} data-testid={`batch-template-col-${col.key}`}>
                            <div className="flex items-center gap-2 mb-2 px-1">
                              <span className={`inline-block w-2 h-2 rounded-full ${ec.bg}`} />
                              <h3 className="text-sm font-semibold text-slate-700">{col.label} Documents</h3>
                              <span className="text-xs text-muted-foreground">({col.items.length})</span>
                            </div>
                            {col.items.length === 0 ? (
                              <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-muted-foreground">
                                No {col.label.toLowerCase()} documents
                              </div>
                            ) : (
                              <div className="space-y-1.5">
                                {col.items.map((template) => {
                                  const isSelected = !!selectedTemplates.find(t => t.id === template.id);
                                  return (
                                    <div
                                      key={template.id}
                                      onClick={() => toggleTemplate(template)}
                                      className={`px-3 py-2 rounded-lg border cursor-pointer transition-all duration-150 overflow-hidden ${
                                        isSelected ? `${ec.border} shadow-sm ${ec.lightBg}` : 'border-border hover:border-primary/40 bg-white'
                                      }`}
                                      data-testid={`template-${template.id}`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <Checkbox
                                          checked={isSelected}
                                          className="flex-shrink-0"
                                          onCheckedChange={() => toggleTemplate(template)}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                        <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${isSelected ? ec.bg : ec.lightBg}`}>
                                          <FileText className={`w-3 h-3 ${isSelected ? 'text-white' : ec.iconColor}`} />
                                        </div>
                                        <h4 className="font-medium text-sm truncate flex-1 min-w-0" title={template.name}>
                                          {template.name}
                                        </h4>
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${ec.bg} ${ec.text} flex-shrink-0 whitespace-nowrap`}>
                                          {ec.label}
                                        </span>
                                      </div>
                                      <div className="text-[11px] text-muted-foreground mt-0.5 pl-14">
                                        {template.fields?.length || 0} fields
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Step 2: Choose Recipient */}
            {currentStep === 1 && (
              <div className="animate-fade-in" data-testid="step-recipient">
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="font-heading text-xl font-semibold">Choose recipient</h2>
                  <div className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${entityColor.bg} ${entityColor.text}`}>
                    {entityType === 'contact' ? <Building2 className="w-3 h-3" /> : <User className="w-3 h-3" />}
                    {entityColor.label}
                  </div>
                </div>
                
                {/* Search */}
                <div className="mb-6">
                  <Label className="mb-2 block">Search Bullhorn</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && searchRecipients()}
                        placeholder="Search by name or email"
                        className={`pl-10 input-focus ${entityColor.border}`}
                        data-testid="recipient-search"
                      />
                    </div>
                    <Button onClick={searchRecipients} disabled={searching || searchQuery.length < 2}>
                      {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Search'}
                    </Button>
                  </div>
                  
                  {searchResults.length > 0 && (
                    <div className="mt-2 border rounded-lg overflow-hidden">
                      {searchResults.map((r) => (
                        <button
                          key={r.id}
                          onClick={() => selectRecipient(r)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-muted transition-colors text-left"
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${entityColor.lightBg}`}>
                            <span className={`font-heading text-sm font-semibold ${entityType === 'contact' ? 'text-[#F9A825]' : 'text-[#4CAF50]'}`}>
                              {r.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{r.name}</p>
                            <p className="text-xs text-muted-foreground">{r.email}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4 my-6">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-sm text-muted-foreground">or enter manually</span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                {/* Manual Entry */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input
                      value={recipient.name}
                      onChange={(e) => setRecipient({ ...recipient, name: e.target.value })}
                      placeholder="John Doe"
                      className="input-focus"
                      data-testid="recipient-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      value={recipient.email}
                      onChange={(e) => setRecipient({ ...recipient, email: e.target.value })}
                      placeholder="john@example.com"
                      className="input-focus"
                      data-testid="recipient-email"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Options */}
            {currentStep === 2 && (
              <div className="animate-fade-in" data-testid="step-options">
                <h2 className="font-heading text-xl font-semibold mb-6">Document Options</h2>
                
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label>Link Expiry</Label>
                    <select
                      value={expiryHours}
                      onChange={(e) => setExpiryHours(parseInt(e.target.value))}
                      className="w-full px-3 py-2 rounded-md border border-border bg-background"
                      data-testid="expiry-select"
                    >
                      <option value={24}>24 hours</option>
                      <option value={48}>48 hours</option>
                      <option value={72}>72 hours (default)</option>
                      <option value={168}>1 week</option>
                      <option value={720}>30 days</option>
                    </select>
                  </div>

                  <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
                    <Checkbox
                      id="requires-approval"
                      checked={requiresApproval}
                      onCheckedChange={setRequiresApproval}
                      data-testid="requires-approval"
                    />
                    <div>
                      <Label htmlFor="requires-approval" className="font-medium text-amber-800 cursor-pointer">
                        Require Admin Approval
                      </Label>
                      <p className="text-sm text-amber-700 mt-1">
                        Completed documents will need your approval before being finalized and synced to Bullhorn
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Message to Recipient (Optional)</Label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Add a personal message..."
                      rows={3}
                      className="w-full px-3 py-2 rounded-md border border-border bg-background resize-none"
                      data-testid="message-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Review & Send */}
            {currentStep === 3 && (
              <div className="animate-fade-in" data-testid="step-review">
                <h2 className="font-heading text-xl font-semibold mb-6">Review & Send</h2>
                
                <div className="space-y-6">
                  {/* Recipient Summary */}
                  <div className="p-4 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${entityColor.bg}`}>
                        {entityType === 'contact' ? <Building2 className="w-5 h-5 text-white" /> : <User className="w-5 h-5 text-white" />}
                      </div>
                      <div>
                        <p className="font-medium">{recipient.name}</p>
                        <p className="text-sm text-muted-foreground">{recipient.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Documents Summary */}
                  <div>
                    <Label className="mb-3 block">Documents to send ({selectedTemplates.length})</Label>
                    <div className="space-y-2">
                      {selectedTemplates.map((t, index) => (
                        <div key={t.id} className="flex items-center gap-3 p-3 rounded-lg border border-border">
                          <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                            {index + 1}
                          </span>
                          <FileText className="w-4 h-4 text-muted-foreground" />
                          <span className="flex-1">{t.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Options Summary */}
                  <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Link expires in</span>
                      <span className="font-medium">{expiryHours} hours</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Requires approval</span>
                      <span className={`font-medium ${requiresApproval ? 'text-amber-600' : 'text-green-600'}`}>
                        {requiresApproval ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              {currentStep < STEPS.length - 1 ? (
                <Button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={
                    (currentStep === 0 && selectedTemplates.length === 0) ||
                    (currentStep === 1 && (!recipient.name || !recipient.email))
                  }
                  className="btn-primary"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSend}
                  disabled={sending}
                  className="btn-primary"
                  data-testid="send-batch-button"
                >
                  {sending ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send {selectedTemplates.length} Documents
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
